import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, Button,
  CircularProgress, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as ProcessIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { payrollApi } from '../../../api/payrollApi';
import {
  PayCycle,
  PayCycleLabels,
  PayrollRunDto,
  PayrollRunStatus,
  PayrollRunStatusLabels,
  PayrollRunStatusColors,
  PayrollGroupAssignmentOptionsResponseDto,
} from '../../../types/payrollTypes';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const addDays = (value: string, days: number) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN');
};

const getPeriodKey = (start?: string | null, end?: string | null) =>
  `${(start ?? '').slice(0, 10)}|${(end ?? '').slice(0, 10)}`;

const PayrollRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<PayrollRunDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payCycle, setPayCycle] = useState<PayCycle>(PayCycle.Monthly);
  const [newMonth, setNewMonth] = useState<number>(new Date().getMonth() + 1);
  const [newYear, setNewYear] = useState<number>(new Date().getFullYear());
  const [weekStart, setWeekStart] = useState<string>(toDateInputValue(new Date()));
  const [departmentId, setDepartmentId] = useState('');
  const [payrollGroupId, setPayrollGroupId] = useState('');
  const [assignmentOptions, setAssignmentOptions] = useState<PayrollGroupAssignmentOptionsResponseDto | null>(null);
  const [creating, setCreating] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const [data, settings] = await Promise.all([
        payrollApi.getPayrollRuns(yearFilter),
        payrollApi.getSettings(),
      ]);
      setRuns(data);
      setPayCycle(settings?.payCycle ?? PayCycle.Monthly);
    } catch {
      toast.error('Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const loadAssignmentOptions = async () => {
    try {
      const data = await payrollApi.getGroupAssignmentOptions();
      setAssignmentOptions(data);
    } catch {
      toast.error('Failed to load payroll scope options');
    }
  };

  const openCreateDialog = () => {
    setDialogOpen(true);
    if (!assignmentOptions) {
      void loadAssignmentOptions();
    }
  };

  const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.response?.data || fallback;

  const getSelectedPeriod = () => {
    if (payCycle === PayCycle.Weekly) {
      if (!weekStart) {
        return { month: 0, year: 0, periodStart: '', periodEnd: '' };
      }
      const [year, month] = weekStart.split('-').map(Number);
      return {
        month,
        year,
        periodStart: weekStart,
        periodEnd: addDays(weekStart, 6),
      };
    }

    const periodStart = `${newYear}-${String(newMonth).padStart(2, '0')}-01`;
    return {
      month: newMonth,
      year: newYear,
      periodStart,
      periodEnd: addDays(addDays(periodStart, 32).slice(0, 8) + '01', -1),
    };
  };

  const isPeriodAlreadyProcessed = (periodStart: string, periodEnd: string, excludeRunId?: string) =>
    runs.some((run) =>
      run.id !== excludeRunId
      && (run.payCycle ?? PayCycle.Monthly) === payCycle
      && getPeriodKey(run.periodStart, run.periodEnd) === getPeriodKey(periodStart, periodEnd)
      && (
        run.status === PayrollRunStatus.Processed
        || run.status === PayrollRunStatus.Locked
        || run.status === PayrollRunStatus.Paid
      )
    );

  const handleCreate = async () => {
    const selectedPeriod = getSelectedPeriod();
    if (selectedPeriod.month < 1 || selectedPeriod.month > 12 || selectedPeriod.year < 2000 || !selectedPeriod.periodStart) {
      toast.error(payCycle === PayCycle.Weekly ? 'Please select a valid week start date' : 'Please select a valid month and year');
      return;
    }

    if (isPeriodAlreadyProcessed(selectedPeriod.periodStart, selectedPeriod.periodEnd)) {
      toast.error('Payroll for this period is already processed.');
      return;
    }

    const existingDraft = runs.find((run) =>
      (run.payCycle ?? PayCycle.Monthly) === payCycle
      && getPeriodKey(run.periodStart, run.periodEnd) === getPeriodKey(selectedPeriod.periodStart, selectedPeriod.periodEnd)
      && (
        run.status === PayrollRunStatus.Draft
        || run.status === PayrollRunStatus.Review
        || run.status === PayrollRunStatus.Approved
      )
    );
    if (existingDraft) {
      toast.info('Payroll draft for this period already exists. Opening the draft preview.');
      setDialogOpen(false);
      navigate(`/payroll/runs/${existingDraft.id}`);
      return;
    }

    setCreating(true);
    try {
      const response = await payrollApi.createPayrollRun(
        selectedPeriod.month,
        selectedPeriod.year,
        departmentId || undefined,
        payrollGroupId || undefined,
        selectedPeriod.periodStart,
        selectedPeriod.periodEnd
      );
      toast.success('Payroll preview created. Review details before processing.');
      setDialogOpen(false);
      void loadRuns();
      navigate(`/payroll/runs/${response.id}`);
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to create payroll run'));
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitReview = async (run: PayrollRunDto) => {
    try {
      await payrollApi.submitPayrollForReview(run.id);
      toast.success('Submitted for review');
      void loadRuns();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to submit payroll run'));
    }
  };

  const handleApprove = async (run: PayrollRunDto) => {
    try {
      await payrollApi.approvePayrollRun(run.id);
      toast.success('Payroll approved');
      void loadRuns();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to approve payroll run'));
    }
  };

  const handleProcess = async (run: PayrollRunDto) => {
    if (isPeriodAlreadyProcessed(run.periodStart, run.periodEnd, run.id)) {
      toast.error('Payroll for this period is already processed.');
      return;
    }

    try {
      await payrollApi.processPayrollRun(run.id);
      toast.success('Payroll run processed');
      void loadRuns();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Failed to process payroll run'));
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await payrollApi.markPayrollRunPaid(id);
      toast.success('Payroll marked as paid');
      void loadRuns();
    } catch {
      toast.error('Failed to mark payroll paid');
    }
  };

  const getRunPeriodLabel = (run: PayrollRunDto) => {
    const runPayCycle = run.payCycle ?? PayCycle.Monthly;
    if (runPayCycle === PayCycle.Weekly) {
      return `${formatDate(run.periodStart)} - ${formatDate(run.periodEnd)}`;
    }

    return `${MONTHS[run.month - 1]} ${run.year}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Payroll Runs</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            size="small"
            label="Year"
            value={yearFilter}
            onChange={(e) => setYearFilter(Number(e.target.value))}
            sx={{ width: 120 }}
          >
            {[...Array(5)].map((_, i) => {
              const y = new Date().getFullYear() - 2 + i;
              return <MenuItem key={y} value={y}>{y}</MenuItem>;
            })}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} sx={{ textTransform: 'none' }}>
            New Payroll Preview
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Total Records: {runs.length}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Run Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Employees</TableCell>
                  <TableCell align="right">Gross Salary</TableCell>
                  <TableCell align="right">Net Salary</TableCell>
                  <TableCell>Processed At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No payroll runs found</TableCell>
                  </TableRow>
                ) : (
                  runs.map((run) => (
                    <TableRow key={run.id}>
                      <TableCell>{PayCycleLabels[run.payCycle ?? PayCycle.Monthly]}</TableCell>
                      <TableCell>{getRunPeriodLabel(run)}</TableCell>
                      <TableCell>
                        <Chip
                          label={PayrollRunStatusLabels[run.status]}
                          color={PayrollRunStatusColors[run.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{run.employeeCount}</TableCell>
                      <TableCell align="right">₹{(run.totalGross ?? 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right">₹{(run.totalNet ?? 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell>{run.processedAt ? new Date(run.processedAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => navigate(`/payroll/runs/${run.id}`)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {run.status === PayrollRunStatus.Draft && (
                            <Tooltip title="Submit for Review">
                              <IconButton size="small" color="primary" onClick={() => handleSubmitReview(run)}>
                                <ProcessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {run.status === PayrollRunStatus.Review && (
                            <Tooltip title="Approve">
                              <IconButton size="small" color="primary" onClick={() => handleApprove(run)}>
                                <ProcessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {run.status === PayrollRunStatus.Approved && (
                            <Tooltip title="Process">
                              <IconButton size="small" color="primary" onClick={() => handleProcess(run)}>
                                <ProcessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {run.status === PayrollRunStatus.Processed && (
                            <Tooltip title="Mark as Paid">
                              <IconButton size="small" color="warning" onClick={() => handleMarkPaid(run.id)}>
                                <LockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Payroll Preview</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Pay Cycle" value={PayCycleLabels[payCycle]} fullWidth disabled />
            {payCycle === PayCycle.Weekly ? (
              <>
                <TextField
                  type="date"
                  label="Week Start"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Week End"
                  value={formatDate(addDays(weekStart, 6))}
                  fullWidth
                  disabled
                />
              </>
            ) : (
              <>
                <TextField
                  select
                  label="Month"
                  value={newMonth}
                  onChange={(e) => setNewMonth(Number(e.target.value))}
                  fullWidth
                >
                  {MONTHS.map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Year"
                  value={newYear}
                  onChange={(e) => setNewYear(Number(e.target.value))}
                  fullWidth
                >
                  {[...Array(5)].map((_, i) => {
                    const y = new Date().getFullYear() - 2 + i;
                    return <MenuItem key={y} value={y}>{y}</MenuItem>;
                  })}
                </TextField>
              </>
            )}
            <TextField
              select
              label="Department"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">All Departments</MenuItem>
              {(assignmentOptions?.departments ?? []).map((department) => (
                <MenuItem key={department.id} value={department.id}>{department.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Payroll Group"
              value={payrollGroupId}
              onChange={(e) => setPayrollGroupId(e.target.value)}
              fullWidth
            >
              <MenuItem value="">All Payroll Groups</MenuItem>
              {(assignmentOptions?.groups ?? []).map((group) => (
                <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating} sx={{ textTransform: 'none' }}>
            {creating ? 'Creating...' : 'Create Preview'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollRunsPage;
